import React, { useContext, useState, useEffect, memo, useRef } from "react";
import { Page, PageContextType, UserProfile } from "../types";
import { PageContext } from "../PageContext";
import { useAuth, playSound } from "../AuthContext";
import BackButton from "../components/BackButton";
import FaceLoginModal from "../components/FaceLoginModal";
import ConfirmationModal from "../components/ConfirmationModal";
import AIConsentModal from "../components/AIConsentModal";
import { Capacitor } from "@capacitor/core";
import { AdService } from "../AdService";

const DEFAULT_THEME_COLOR = "hsl(259, 55%, 20%)";

const mapSliderValueToColor = (value: number): string => {
  const stops = [
    { val: 0, h: 259, s: 55, l: 20 },
    { val: 20, h: 275, s: 80, l: 55 },
    { val: 40, h: 320, s: 70, l: 80 },
    { val: 60, h: 45, s: 95, l: 60 },
    { val: 80, h: 335, s: 80, l: 55 },
    { val: 100, h: 0, s: 0, l: 100 },
  ];

  let startStop = stops[0];
  let endStop = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (value >= stops[i].val && value <= stops[i + 1].val) {
      startStop = stops[i];
      endStop = stops[i + 1];
      break;
    }
  }

  const range = endStop.val - startStop.val;
  const percentage = range === 0 ? 0 : (value - startStop.val) / range;

  let h = startStop.h + (endStop.h - startStop.h) * percentage;
  if (Math.abs(endStop.h - startStop.h) > 180) {
    const startHue = startStop.h;
    let endHue = endStop.h;
    if (endHue < startHue) endHue += 360;
    h = (startHue + (endHue - startHue) * percentage) % 360;
  }

  const s = startStop.s + (endStop.s - startStop.s) * percentage;
  const l = startStop.l + (endStop.l - startStop.l) * percentage;

  return `hsl(${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`;
};

const mapColorToSliderValue = (color: string): number => {
  try {
    if (color === DEFAULT_THEME_COLOR) return 0;
    const matches = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!matches) return 0;
    const [, h, s, l] = matches.map(Number);
    if (l === 100 && s === 0) return 100;
    if (l < 50) return 0;
    if (h > 270 && h < 330) return 20 + (330 - h) / 2;
    if (h > 330 || h < 40) return 80;
    if (h >= 40 && h < 60) return 60;
    return 40;
  } catch {
    return 0;
  }
};

const SectionHeader = memo<{ title: string; colorClass?: string }>(
  ({ title, colorClass = "" }) => (
    <h3 className={`text-lg font-extrabold tracking-wide ${colorClass}`}>
      <span className={!colorClass ? "aurora-text" : ""}>
        {title.toUpperCase()}
      </span>
    </h3>
  )
);

const SettingsLink = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    children: React.ReactNode;
    danger?: boolean;
    extremeDanger?: boolean;
  }
>(({ onClick, children, danger, extremeDanger }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className={`w-full text-left p-3 flex justify-between items-center hover:bg-gray-100 rounded-lg font-semibold transition-colors ${
      extremeDanger
        ? "text-red-800 bg-red-100 hover:bg-red-200"
        : danger
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-700"
    }`}
  >
    <span>{children}</span>
    <i
      className={`ph-duotone ${
        danger || extremeDanger ? "ph-warning" : "ph-caret-right"
      } ${
        extremeDanger
          ? "text-red-800"
          : danger
          ? "text-red-400"
          : "text-gray-400"
      }`}
    ></i>
  </button>
));

const SettingsToggle = memo<{
  label: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  id: string;
  children?: React.ReactNode;
}>(({ label, checked, onChange, id, children }) => (
  <div className="flex justify-between items-center p-3">
    <label
      htmlFor={id}
      className="text-gray-700 font-semibold flex items-center gap-2"
    >
      {children || label}
    </label>
    <input type="checkbox" id={id} checked={checked} onChange={onChange} />
  </div>
));

const SettingsSelect = memo<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  id: string;
  children: React.ReactNode;
}>(({ label, value, onChange, id, children }) => (
  <div className="flex justify-between items-center p-3">
    <label htmlFor={id} className="text-gray-700 font-semibold">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={onChange}
      className="text-sm border-gray-300 rounded-lg shadow-sm bg-gray-50 py-1"
    >
      {children}
    </select>
  </div>
));

const SettingsPage: React.FC = () => {
  const isIOS = Capacitor.getPlatform() === "ios";
  const playStoreAccountName = ['G', 'o', 'o', 'g', 'l', 'e', ' ', 'P', 'l', 'a', 'y', ' ', 'A', 'c', 'c', 'o', 'u', 'n', 't'].join('');

  const pageContext = useContext(PageContext);
  const {
    userProfile,
    updateSettings,
    disableFaceLogin,
    logout,
    deleteAccount,
    purchaseStoreItem,
  } = useAuth();
  const [isFaceRegisterModalOpen, setFaceRegisterModalOpen] = useState(false);
  const [useDefaultTheme, setUseDefaultTheme] = useState(true);
  const [themeColor, setThemeColor] = useState(DEFAULT_THEME_COLOR);
  const [themeSliderValue, setThemeSliderValue] = useState(0);
  const [showAIConsentModal, setShowAIConsentModal] = useState(false);

  // Confirmation States
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteButtonRect, setDeleteButtonRect] = useState<DOMRect | null>(
    null
  );

  useEffect(() => {
    if (userProfile) {
      const primary = userProfile.primaryColor || DEFAULT_THEME_COLOR;
      const isDefault = primary === DEFAULT_THEME_COLOR;
      setUseDefaultTheme(isDefault);
      setThemeColor(primary);
      setThemeSliderValue(mapColorToSliderValue(primary));
    }
  }, [userProfile]);

  const handleDefaultThemeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setUseDefaultTheme(isChecked);
    if (isChecked) {
      setThemeColor(DEFAULT_THEME_COLOR);
      setThemeSliderValue(0);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleDeleteAccountClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDeleteButtonRect(e.currentTarget.getBoundingClientRect());
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteAccount();
      setDeleteModalOpen(false);
      // AuthContext state change will trigger navigation to Login
    } catch (error: any) {
      alert(error.message);
      setDeleteModalOpen(false);
    }
  };

  const handleShareApp = async () => {
    // Use live URL if native to ensure friends can actually open it
    const isNative = Capacitor.isNativePlatform();
    const url = isNative
      ? "https://what-was-it-gc-fb-56459.web.app"
      : window.location.origin;

    const text = "Check out this cool guessing game: What Was It?";
    if (navigator.share) {
      try {
        await navigator.share({ title: "What Was It?", text, url });
      } catch (e) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      alert("App link copied to clipboard!");
    }
  };

  const sliderThumbStyle = ` #theme-slider::-webkit-slider-thumb { background: ${themeColor}; } #theme-slider::-moz-range-thumb { background: ${themeColor}; } `;

  return (
    <>
      <div className="space-y-6 pb-12">
        <style>{sliderThumbStyle}</style>
        <BackButton />
        <div className="bg-white p-4 space-y-2 rounded-xl shadow-md">
          <SectionHeader title="Account" />
          <SettingsLink
            onClick={(e) =>
              pageContext?.launchEditProfileModal(
                e.currentTarget.getBoundingClientRect()
              )
            }
          >
            Edit Profile
          </SettingsLink>
          <SettingsLink
            onClick={() =>
              alert(
                'To change password, please log out and use "Forgot Password" on the login screen.'
              )
            }
          >
            Change Password
          </SettingsLink>
        </div>
        <div className="bg-white p-4 space-y-2 rounded-xl shadow-md">
          <SectionHeader title="Security" />
          <SettingsToggle
            label="Enable Face Login"
            checked={userProfile?.isFaceLoginEnabled ?? false}
            onChange={(e) =>
              e.target.checked
                ? setFaceRegisterModalOpen(true)
                : disableFaceLogin()
            }
            id="face-login-toggle"
          >
            Enable Face Login{" "}
            <i className="ph-duotone ph-face-id text-gray-500"></i>
          </SettingsToggle>
        </div>
        <div className="bg-white p-4 space-y-4 rounded-xl shadow-md">
          <SectionHeader title="Custom Theme" />
          <SettingsToggle
            label="Use Default Theme"
            checked={useDefaultTheme}
            onChange={handleDefaultThemeToggle}
            id="default-theme-toggle"
          />
          <div
            className={`space-y-4 border-t pt-4 mt-2 transition-opacity ${
              useDefaultTheme ? "opacity-50" : ""
            }`}
          >
            <div className="p-3">
              <label
                htmlFor="theme-slider"
                className="text-gray-700 font-semibold mb-2 block"
              >
                Select your main theme color
              </label>
              <input
                id="theme-slider"
                type="range"
                min="0"
                max="100"
                value={themeSliderValue}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setUseDefaultTheme(false);
                  setThemeSliderValue(v);
                  setThemeColor(mapSliderValueToColor(v));
                }}
                className="w-full color-slider mt-2"
                disabled={useDefaultTheme}
              />
            </div>
          </div>
          <button
            onClick={() =>
              updateSettings({
                primaryColor: useDefaultTheme
                  ? DEFAULT_THEME_COLOR
                  : themeColor,
              })
            }
            className="w-full mt-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:opacity-90 transition-opacity"
          >
            Apply Theme Color
          </button>
        </div>
        <div className="bg-white p-4 space-y-2 rounded-xl shadow-md">
          <SectionHeader title="Notifications" />
          <SettingsToggle
            label="In-App Notifications"
            checked={userProfile?.inAppNotificationsEnabled ?? true}
            onChange={(e) => updateSettings({ inAppNotificationsEnabled: e.target.checked })}
            id="in-app-notifications-toggle"
          >
            In-App Notifications{" "}
            <i className="ph-duotone ph-bell-ringing text-gray-500"></i>
          </SettingsToggle>
        </div>
        <div className="bg-white p-4 space-y-2 rounded-xl shadow-md">
          <SectionHeader title="Privacy" />
          <SettingsToggle
            label="Appear Offline"
            checked={userProfile?.appearOffline ?? false}
            onChange={(e) =>
              updateSettings({ appearOffline: e.target.checked })
            }
            id="appear-offline-toggle"
          >
            Appear Offline{" "}
            <i className="ph-duotone ph-eye-slash text-gray-500"></i>
          </SettingsToggle>
          <SettingsToggle
            label="Private Account"
            checked={userProfile?.isPrivate ?? false}
            onChange={(e) => updateSettings({ isPrivate: e.target.checked })}
            id="private-account-toggle"
          >
            Private Account{" "}
            <i className="ph-duotone ph-lock-simple text-gray-500"></i>
          </SettingsToggle>
          <SettingsToggle
            label="AI Features & Privacy"
            checked={userProfile?.aiConsentEnabled ?? false}
            onChange={(e) => updateSettings({ aiConsentEnabled: e.target.checked })}
            id="ai-consent-toggle"
          >
            AI Features & Privacy{" "}
            <i className="ph-duotone ph-brain text-purple-600"></i>
          </SettingsToggle>
          <SettingsLink
            onClick={() => setShowAIConsentModal(true)}
          >
            Review AI Privacy Disclosure
          </SettingsLink>
          <SettingsLink
            onClick={() =>
              pageContext?.switchPage(Page.BlockedUsers, "Blocked Users")
            }
          >
            Blocked Users
          </SettingsLink>
          {Capacitor.isNativePlatform() &&
            import.meta.env.VITE_ENABLE_ADS === "true" && (
              <SettingsLink
                onClick={() =>
                  import("../AdService").then((m) =>
                    m.AdService.showConsentForm()
                  )
                }
              >
                Manage Ad Consent{" "}
                <i className="ph-duotone ph-shield-check text-gray-500 ml-1"></i>
              </SettingsLink>
            )}
        </div>
        <div className="bg-white p-4 space-y-2 rounded-xl shadow-md">
          <SectionHeader title="Sounds" />
          <SettingsSelect
            label="Correct Guess Sound"
            id="correct-guess-sound-select"
            value={userProfile?.correctGuessSound || "None"}
            onChange={(e) =>
              updateSettings({ correctGuessSound: e.target.value })
            }
          >
            <option>Ascend</option>
            <option>Arcade</option>
            <option>Triumph</option>
            <option>Success Chime</option>
            <option>None</option>
          </SettingsSelect>
        </div>
        <div className="bg-white p-4 space-y-3 rounded-xl shadow-md">
          <SectionHeader title="Help & Support" />
          <SettingsLink
            onClick={() => pageContext?.switchPage(Page.AISupport, "Support")}
          >
            Report a Problem
          </SettingsLink>
          <SettingsLink
            onClick={() => pageContext?.switchPage(Page.AboutUs, "About Us")}
          >
            About Us
          </SettingsLink>
          <SettingsLink onClick={handleShareApp}>Share App Link</SettingsLink>
          
          <div className="pt-3 border-t border-gray-100 space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <i className="ph-duotone ph-question text-purple-600 text-sm"></i>
              <span>Purchase Support & Troubleshooting</span>
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Having trouble buying clues or credits? {isIOS ? (
                <>You must be signed into an active <span className="font-semibold text-gray-900">Apple ID</span> (on iOS/TestFlight) on your mobile device to complete in-app purchases.</>
              ) : (
                <>You must be signed into an active <span className="font-semibold text-gray-900">Apple ID</span> (on iOS/TestFlight) or <span className="font-semibold text-gray-900">{playStoreAccountName}</span> (on Android) on your mobile device to complete in-app purchases.</>
              )}
            </p>
          </div>
        </div>

        {/* Temporarily hidden for app store review */}
        <div className="hidden bg-white p-4 space-y-4 rounded-xl shadow-md border-2 border-blue-100">
          <SectionHeader title="Business Center" colorClass="text-blue-600" />
          {!userProfile?.isBusiness ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Grow your brand with sponsored puzzles and direct links to your
                website.
              </p>
              <ul className="text-xs text-gray-500 space-y-1 list-disc pl-4">
                <li>Post Sponsored Puzzles with links & coupons</li>
                <li>Get a verified Business Badge</li>
                <li>Higher visibility in the feed</li>
              </ul>
              <button
                onClick={async () => {
                  if (
                    window.confirm(
                      "Upgrade to Business Account? This will unlock sponsored puzzle features. Cost is £9.99/mo."
                    )
                  ) {
                    const success = await purchaseStoreItem(
                      "business",
                      1,
                      "£9.99/mo"
                    );
                    if (!success) {
                      alert(
                        "Unable to process the payment at this time or purchases are disabled on this platform."
                      );
                    }
                  }
                }}
                className="w-full bg-blue-600 text-white font-bold py-2 rounded-lg shadow hover:bg-blue-700 transition-colors flex justify-between px-4 items-center"
              >
                <span>Upgrade to Business</span>
                <span>£9.99 / mo</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-600 font-bold">
                <i className="ph-fill ph-check-circle"></i>
                <span>Business Account Active</span>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-400">
                    Business Website
                  </label>
                  <input
                    type="url"
                    value={userProfile.businessWebsite || ""}
                    onChange={(e) =>
                      updateSettings({ businessWebsite: e.target.value })
                    }
                    placeholder="https://yourbrand.com"
                    className="mt-1 block w-full border-gray-200 rounded-md shadow-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400">
                    Call to Action Text
                  </label>
                  <input
                    type="text"
                    value={userProfile.businessActionText || "Visit Website"}
                    onChange={(e) =>
                      updateSettings({ businessActionText: e.target.value })
                    }
                    placeholder="Visit Website"
                    className="mt-1 block w-full border-gray-200 rounded-md shadow-sm text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 italic">
                You can now mark your puzzles as 'Sponsored' in the Create Post
                page.
              </p>
            </div>
          )}
        </div>

        <div className="bg-white p-4 rounded-xl shadow-md space-y-2">
          <SettingsLink onClick={handleLogout} danger={true}>
            Log Out
          </SettingsLink>
          <SettingsLink onClick={handleDeleteAccountClick} extremeDanger={true}>
            Delete Account
          </SettingsLink>
        </div>
      </div>
      <FaceLoginModal
        isOpen={isFaceRegisterModalOpen}
        onClose={() => setFaceRegisterModalOpen(false)}
        mode="register"
        onSuccess={() => setFaceRegisterModalOpen(false)}
        onError={(m) => {
          console.error(m);
          setFaceRegisterModalOpen(false);
        }}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Account?"
        message="Are you sure? This will permanently delete your profile, puzzles, and all data. This cannot be undone."
        confirmText="Delete Forever"
        triggerRect={deleteButtonRect}
      />

      <AIConsentModal
        isOpen={showAIConsentModal}
        onClose={() => setShowAIConsentModal(false)}
        onConsentGranted={() => updateSettings({ aiConsentEnabled: true })}
      />
    </>
  );
};

export default SettingsPage;
